import {
  Airline,
  Airport,
  BarcodeLeadsFlight,
  BarcodeLeadsFlights,
  FlightSelectorFlight,
} from "../../types/intake-funnel";
import { RawAirline } from "../../../api/airlines";
import { RawAirport } from "../../../api/airports";
import { RawFlight } from "v2/api/flights";
import { RawBarcodeLeadsFlights } from "v2/api/barcode-leads";
import { Nullable } from "__tests-utils__/types";
import { parseDate } from "v2/utils/parseDate";

export function extractAirlineData(rawAirline: RawAirline): Airline {
  return {
    iata: rawAirline.iata,
    icao: rawAirline.icao,
    name: rawAirline.name,
    code: rawAirline.code || "",
    ec261: rawAirline.ec261,
    countryCode: rawAirline.country_code,
    displayName: rawAirline.display_name,
    uniqueIdentifier: rawAirline.unique_identifier,
  };
}

export function extractAirportData(rawAirport: RawAirport): Airport {
  return {
    iata: rawAirport.iata,
    name: rawAirport.name,
    rank: rawAirport.rank,
    city: rawAirport.city,
    ec261: rawAirport.ec261,
    country: rawAirport.country,
    countryCode: rawAirport.country_code,
    timeZone: rawAirport.time_zone,
    latitude: rawAirport.latitude,
    longitude: rawAirport.longitude,
    searchText: rawAirport.search_text,
    cityTranslations: rawAirport.city_translations,
  };
}

export function extractFlightSelectorFlightData(
  rawFlight: RawFlight,
): Nullable<FlightSelectorFlight> {
  return {
    flightNumber: rawFlight.flight_number,
    localDepartureAt: parseDate(rawFlight.local_departure_at) || null,
    localArrivalAt: parseDate(rawFlight.local_arrival_at) || null,
    airlineCode: rawFlight.airline_code,
    airlineIdentifier: rawFlight.airline_identifier,
    airlineName: rawFlight.airline_name,
    operatingAirlineCode: rawFlight.operating_airline_code,
    humanizedArrivalStatus: rawFlight.humanized_arrival_status,
  };
}

export function extractBarcodeFlightsData(data: RawBarcodeLeadsFlights): BarcodeLeadsFlights {
  const rawEligibilityCheckResult = data?.eligibility_check_result;
  const rawBarcodeLeadsItineraryFlights = data?.itinerary?.flights;

  const potentialRegulation = data?.potential_regulation || undefined;

  if (!rawBarcodeLeadsItineraryFlights || rawBarcodeLeadsItineraryFlights.length === 0) {
    throw new Error("No itinerary found in barcode leads response");
  }

  const barcodeLeadsItineraryFlights = rawBarcodeLeadsItineraryFlights.map(
    (rawBarcodeLeadsItinerary): BarcodeLeadsFlight => ({
      disrupted: rawBarcodeLeadsItinerary?.disrupted || undefined,
      // It's impossible for localDepartureDate to be null or undefined here, but TS doesn't know that
      localDepartureDate: parseDate(rawBarcodeLeadsItinerary.local_departure_date) || new Date(),
      flightIdentifier: rawBarcodeLeadsItinerary?.flight_identifier || undefined,
      arrivalTime: rawBarcodeLeadsItinerary.arrival_time || undefined,
      departureTime: rawBarcodeLeadsItinerary.departure_time || undefined,
      actualArrivalTime: rawBarcodeLeadsItinerary.actual_arrival_time || undefined,
      actualDepartureTime: rawBarcodeLeadsItinerary.actual_departure_time || undefined,
      informed:
        rawBarcodeLeadsItinerary?.informed !== undefined
          ? rawBarcodeLeadsItinerary.informed
          : undefined,
      flightNumber: rawBarcodeLeadsItinerary.flight_number,
      delayMins: rawBarcodeLeadsItinerary?.delay_mins || undefined,
      distanceKm: rawBarcodeLeadsItinerary?.distance_km || undefined,
      recognized: rawBarcodeLeadsItinerary.recognized,
      airline: rawBarcodeLeadsItinerary.airline
        ? {
            iata: rawBarcodeLeadsItinerary.airline.iata,
            name: rawBarcodeLeadsItinerary.airline.name,
            uniqueIdentifier: rawBarcodeLeadsItinerary.airline.unique_identifier,
          }
        : undefined,
      arrivalAirport: {
        city: rawBarcodeLeadsItinerary.arrival_airport.city,
        iata: rawBarcodeLeadsItinerary.arrival_airport.iata,
        name: rawBarcodeLeadsItinerary.arrival_airport.name,
      },
      departureAirport: {
        city: rawBarcodeLeadsItinerary.departure_airport.city,
        iata: rawBarcodeLeadsItinerary.departure_airport.iata,
        name: rawBarcodeLeadsItinerary.departure_airport.name,
      },
    }),
  );

  const disruptedFlightIdentifier = barcodeLeadsItineraryFlights.find(
    (flight) => flight.disrupted,
  )?.flightIdentifier;

  const eligibilityCheckResult = {
    finalEligibility: rawEligibilityCheckResult?.final_eligibility || undefined,
    disruptionReason: rawEligibilityCheckResult?.disruption_reason || undefined,
    compensationInCurrencies: rawEligibilityCheckResult?.compensation_in_currencies || undefined,
    rejectionReason: rawEligibilityCheckResult?.rejection_reason,
    disruptedFlightIdentifier: disruptedFlightIdentifier,
    standard: rawEligibilityCheckResult?.standard,
  };

  return {
    flights: barcodeLeadsItineraryFlights,
    potentialRegulation,
    eligibilityCheckResult,
  };
}
